import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { AssignmentsListResponse, DriverAssignment } from './types';

const KEY = {
  all: ['driver-assignments'] as const,
  byVehicle: (vehicleId: string) => ['driver-assignments', 'vehicle', vehicleId] as const,
  byDriver: (driverId: string) => ['driver-assignments', 'driver', driverId] as const,
};

export function useAssignmentsByVehicle(vehicleId: string, enabled = true) {
  return useQuery<AssignmentsListResponse>({
    queryKey: KEY.byVehicle(vehicleId),
    enabled: enabled && !!vehicleId,
    queryFn: async () => {
      const { data } = await api.get<AssignmentsListResponse>('/driver-assignments', {
        params: { vehicleId },
      });
      return data;
    },
  });
}

export function useAssignmentsByDriver(driverId: string, enabled = true) {
  return useQuery<AssignmentsListResponse>({
    queryKey: KEY.byDriver(driverId),
    enabled: enabled && !!driverId,
    queryFn: async () => {
      const { data } = await api.get<AssignmentsListResponse>('/driver-assignments', {
        params: { driverId },
      });
      return data;
    },
  });
}

export function useCreateAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      driverId: string;
      vehicleId: string;
      startedAt?: string;
      notes?: string;
    }) => {
      const { data } = await api.post<{ data: DriverAssignment }>('/driver-assignments', input);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY.all });
    },
  });
}

export function useEndAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, endedAt }: { id: string; endedAt?: string }) => {
      const { data } = await api.post<{ data: DriverAssignment }>(
        `/driver-assignments/${id}/end`,
        { endedAt },
      );
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY.all });
    },
  });
}
